import {
  Meeting,
  OrderMeeting,
  ApprovalOrderMeeting,
  OrderMembers,
  AttendState,
  MeetingRecord,
  MeetingFile,
  MeetFileUploadRes,
  MeetingSummary,
  MeetingIotMsg,
  IMeetingCalendarDetailList,
  OrderMeetingV2,
  OrderSettings,
  IRelations,
  IMeetingFile,
  AllMeetingStatus
} from 'type/meeting';
import { stringify } from 'qs';
import Request from './request';
import { DefaultSuccess } from 'type/http';
import { getUserId, getCompanyId, getMobile } from 'data/storage';
import { IMeetingDetail } from 'type/meetingDetail';
import { IMemberScheduleDetail } from 'nicehubs-plus';

const request = new Request('meeting');
const airRequest = new Request('airdisk');
const triggerRequest = new Request('trigger');
const bffRequest = new Request('');

/**
 * 查询会议
 * @param meetingId 会议id
 */
export const searchMeeting = (meetingId: string) => {
  return request.get<Meeting>(`/api/user/meetings/${meetingId}?needMeetingAuditRecord=true`);
};

/**
 * 查询审批会议（废弃）
 */
export const searchApprovalMeeting = (meetingId: string) => {
  return request.get<{ data: Meeting }>(`/api/user/meeting-audit-records/${meetingId}/meeting-view`);
};

/**
 * 预约会议（废弃）
 * @param meeting 会议信息
 */
export const orderMeeting = (meeting: OrderMeeting) => {
  return request.post<Meeting>('/api/user/meetings/actions/schedule', {
    ...meeting
  });
};

/**
 * 预约审批会议（废弃）
 */
export const orderApprovalMeeting = (meeting: ApprovalOrderMeeting) => {
  return request.post<Meeting>('/api/user/meeting-audit-records', {
    ...meeting
  });
};

/**
 * 获取会议详情
 * @param meetingId 会议id
 */
export const getMeetingDetailsV2 = (meetingNo: string) => {
  return request.get<DefaultSuccess<IMeetingDetail>>(`/api/user/v2/meetings/${meetingNo}`);
};

/**
 * 修改会议（废弃）
 * @param meeting 会议信息
 */
export const updateMeeting = (meetingId: string, meeting: OrderMeeting) => {
  return request.put<DefaultSuccess>(`/api/user/meetings/${meetingId}`, {
    ...meeting
  });
};

enum EffectiveType {
  DEFAULT = 0,
  ONLY_CURRENT = 1,
  ALL = 2
}
/**
 * 取消会议
 * @param meetingId 会议id
 * @param effectiveType 重复生效范围，1-仅对当前会议生效，2-对当前及后续会议生效
 */
export const cancelMeeting = (meetingId: string, effectiveType: EffectiveType = 1) => {
  return request.put<DefaultSuccess>(`/api/user/meetings/${meetingId}/state/cancel?effectiveType=${effectiveType}`, {});
};

/**
 * 取消周期会议中本次会议
 */
export const cancelThisMeeting = (meetingNo: string) => {
  return request.post<DefaultSuccess>(`/api/user/v2/repeat-meetings/${meetingNo}/actions/cancel-single`);
};

/**
 * 取消所有周期会议
 */
export const cancelAllMeeting = (meetingInfo: { repeatMeetingNo: string; startTimeGe?: number }) => {
  return request.post<DefaultSuccess>(`/api/user/v2/repeat-meetings/${meetingInfo.repeatMeetingNo}/actions/cancel-repeat`, {
    startTimeGe: meetingInfo.startTimeGe
  });
};

/**
 * 撤销审批会议
 */
// export const revokeMeeting = (meetingAuditRecordId: string) => {
//   return request.post<DefaultSuccess>(`/api/user/meeting-audit-records/${meetingAuditRecordId}/actions/revoke`);
// };

/**
 * 撤销审批会议
 */
export const revokeMeeting = (meetingAuditRecordId: string) => {
  return request.post<DefaultSuccess>(`/api/user/v2/meetings/${meetingAuditRecordId}/actions/revoke`);
};

export type ChangeMemberStatusParams = {
  meetingId: string;
  state: AttendState;
  reason?: string;
  effectiveType?: EffectiveType;
};

export const changeMemberStatus = ({ meetingId, state, reason, effectiveType }: ChangeMemberStatusParams) => {
  return request.put<DefaultSuccess>(`/api/user/meetings/${meetingId}/members/status`, {
    state: state,
    reason: reason || '',
    effectiveType: effectiveType || 0
  });
};

/**
 * 删除会议
 * @param meetingId 会议Id
 * @returns
 */
export const deleteMeeting = (meetingId: string) => {
  return request.delete<DefaultSuccess>(`/api/user/meetings/meeting_records/${meetingId}`);
};

/**
 * 结束会议
 * @param meetingId 会议Id
 * @returns
 */
export const closeMeeting = (meetingId: string) => {
  return request.put<DefaultSuccess>(`/api/user/meetings/${meetingId}/state/finish`);
};

/**
 * 退出会议
 * @param meetingId 会议Id
 * @param effectiveType
 * @returns
 */
export const quitMeeting = (meetingId: string, effectiveType?: EffectiveType) => {
  return request.delete<DefaultSuccess>(`/api/user/meetings/${meetingId}/members`, {
    data: {
      effectiveType: effectiveType || 0
    }
  });
};

/**
 * 添加参会人
 * @param meetingId 会议Id
 * @param members 参会人列表
 */
export const addMeetingMember = (meetingId: string, members: OrderMembers) => {
  return request.post<DefaultSuccess>(`/api/user/meetings/${meetingId}/members`, {
    ...members
  });
};

/**
 * 删除参会人
 * @param meetingId 会议Id
 * @param memberIds 参会人Id
 */
export const deleteMeetingMember = (meetingId: string, memberIds: { ids: string[] }) => {
  return request.post<DefaultSuccess>(`/api/user/meetings/${meetingId}/members/actions/delete`, {
    ...memberIds
  });
};

export const getFilesInfo = (shareId: string, accessKey: string) => {
  // TODO 返回空数据
  return Promise.resolve([]);
  // return airRequest.get<MeetingFile[]>(`/api/shares/${shareId}/resources`, {
  //   headers: {
  //     'x-share-key': accessKey
  //   }
  // });
};

/**
 * 添加会议文件
 * @param meetingId 会议Id
 * @param formData 文件form
 */
export const addMeetingFile = (meetingId: string, data: MeetFileUploadRes[]) => {
  const params = data.map((d) => ({ ...d, fileId: d.id }));
  return request.post<DefaultSuccess>(`/api/user/meetings/${meetingId}/files`, params);
};

/**
 * 删除会议文件
 * @param meetingId 会议Id
 * @param memberIds 参会人Id
 */
export const deleteMeetingFile = (meetingId: string, fileIds: string[]) => {
  return request.delete<DefaultSuccess>(`/api/user/meetings/${meetingId}/files`, { data: { fileIds } });
};

export type GetMeetingsByRoom = {
  roomIds?: string;
  startTime: number | string;
  endTime: number | string;
  needMeetingAuditRecord?: boolean;
};

/**
 * 根据会议室ID和时间查询会议列表
 */
export const getMeetingsByRoom = (params: GetMeetingsByRoom) => {
  params.needMeetingAuditRecord = true;
  return request.get<Meeting[]>(`/api/user/meetings?${stringify(params)}`);
};

export type GetMeetingByUserId = {
  beginTime: number;
  endTime: number;
  markConflict?: boolean;
  needMeetingAuditRecord?: boolean;
};

export type GetMeetingRecordPage = GetMeetingByUserId & {
  meetingStatusNotIn?: AllMeetingStatus[];
  pageNo?: number;
  pageSize?: number;
};

export type MeetingRecordPage = {
  pageNo: number;
  pageSize: number;
  total: number;
  data: MeetingRecord[];
  sortDirection?: string | null;
  sortKey?: string | null;
};

/**
 * 获取某用户在某个时间段下的会议记录
 * @param userId 用户ID
 * @param params
 * @returns
 */
export const getMeetingByUserId = (params: GetMeetingByUserId) => {
  params.needMeetingAuditRecord = true;
  // return request.get<MeetingRecord[]>(`/api/users/${getUserId()}/meeting_records?${stringify(params)}`);
  // todo 待联调
  return request.get<{ data: MeetingRecord[] }>(`/api/user/meeting-records/list?${stringify(params)}`);
};

/**
 * 分页查询会议记录
 */
export const getMeetingRecordPage = (params: GetMeetingRecordPage) => {
  return request.post<DefaultSuccess<MeetingRecordPage>>('/api/user/meeting-records/page', params);
};

/**
 *查询日历下的日程
 * @param userId 用户ID
 * @param params
 * @returns
 */
export const getMeetingRecordOfCalendarList = (params: GetMeetingByUserId) => {
  return request.get<DefaultSuccess<IMeetingCalendarDetailList[]>>(`/api/user/meeting-records/calendar/list?${stringify(params)}`);
};

/**
 * 发布会议纪要
 * @param meetingId
 * @param subject
 * @param content
 * @returns
 */
export const publicSummary = (meetingId: string, subject: string, content: string) => {
  return request.put<DefaultSuccess>(`/api/meeting_summaries/${meetingId}/state/publish`, {
    content,
    subject
  });
};

/**
 * 发布会议议程
 * @param meetingId
 * @param subject
 * @param content
 * @returns
 */
export const publicContent = (meetingNo: string, content: string) => {
  return request.post<DefaultSuccess>(`/api/user/v2/meetings/meeting/${meetingNo}/discuss-content`, JSON.stringify({ content }), {
    headers: {
      'Content-Type': 'application/json' // 设置请求头
    }
  });
};

/**
 * 查看纪要
 * @param meetingId
 * @returns
 */
export const getSummary = (meetingId: string) => {
  return request.get<MeetingSummary>(`/api/user/v2/meeting/${meetingId}/summary`);
};

/**
 * 查询会议室设备状态
 * @param meetingId
 * @returns
 */
export const getIotDevices = (meetingId: string) => {
  return request.get<MeetingIotMsg[]>(`/api/thirdparty/meetings/${meetingId}/device-type`);
};

/**
 * 会议室签到
 * @param meetingId 会议号
 */
export const signMeeting = (meetingId: string) => {
  return request.post<DefaultSuccess>(`/api/user/meetings/${meetingId}/actions/sign`);
};

/**
 * 获取帮助信息
 */
export const getHelpMsg = () => {
  return request.get(`/api/client/app-configurations?companyId=${getCompanyId()}&appId=com.maxhub.webteams.help`);
};

/**
 * 提交反馈意见
 */
export const setFeedbackMsg = (feedbackMsg: string) => {
  return airRequest.post('/api/user/issues', {
    type: '反馈意见',
    userId: getUserId(),
    mobile: getMobile(),
    productId: '5e601968f69f414a9ef7bbb11b111cb6',
    content: feedbackMsg
  });
};

/**
 * 获取发送给我的审批记录未读数
 */
export const getUnreadApprovalMeeting = () => {
  return request.get<{ data: number }>('/api/user/v2/meeting-audit-records/send-to-me/un-read-count');
};

interface ApprovalMeetingParams {
  pageNo: number;
  pageSize: number;
}

/**
 * 我发起的会议审批记录
 */
export const getInitiatedApprovalMeeting = (params?: ApprovalMeetingParams) => {
  return request.post<{ data: { data: any[] } }>('/api/user/v2/meeting-audit-records/send-by-me/page', {
    ...params
  });
};

/**
 * 我收到的会议审批记录
 */
export const getReceivedApprovalMeeting = (params?: ApprovalMeetingParams) => {
  return request.post<{ data: { data: any[] } }>('/api/user/v2/meeting-audit-records/send-to-me/page', {
    ...params
  });
};

/**
 * 通过审批
 */
export const setApprovalMeetingPass = (meetingNo: string, meetingAuditRecordId: string) => {
  return request.post<any>(`/api/user/v2/meeting-audit-records/${meetingNo}/${meetingAuditRecordId}/actions/agree`);
};
/**
 * 不通过审批
 */
export const setApprovalMeetingFail = (meetingNo: string, meetingAuditRecordId: string, reason: string) => {
  return request.post<any>(`/api/user/v2/meeting-audit-records/${meetingNo}/${meetingAuditRecordId}/actions/refuse`, {
    refuseReason: reason
  });
};

/**
 * 根据审批id查询详情
 */
export const getApprovalMeeting = (meetingNo: string) => {
  return request.get<any>(`/api/user/v2/meeting-audit-records/${meetingNo}`);
};

/**
 * 读会议审批记录
 */
export const setApprovalMeetingRead = (meetingNo: string) => {
  return request.post<any>(`/api/user/v2/meeting-audit-records/${meetingNo}/actions/read`);
};

/**
 * 继续会议（自动化指令触发会议自动结束，在倒计时结束前可选择继续会议）
 */
export const continueMeeting = (meetingId: string) => {
  return request.post<DefaultSuccess>(`/api/user/meetings/${meetingId}/operations?operationType=CONTINUE_MEETING`);
};

export type ExtendMeetingParams = {
  newEndTime: number;
};

export type ExtendMeetingResponse<T = unknown> = {
  code?: number;
  message?: string;
  data?: T;
  success?: boolean;
};

/**
 * 延长会议
 */
export const extendMeeting = <T = unknown>(meetingNo: string, data: ExtendMeetingParams) => {
  return request.post<ExtendMeetingResponse<T>>(`/api/user/v2/meetings/${meetingNo}/actions/extend`, data);
};

/**
 * 查询会议继续会议操作记录
 */
export const getContinueMeetingOperation = (meetingId: string) => {
  return request.get<DefaultSuccess<any[]>>(`/api/user/meetings/${meetingId}/operations?operationType=CONTINUE_MEETING`);
};

/**
 * 查询会议主题配置信息
 */
export const getCompanyconfigs = (moduleKey = 'themePublicScope') => {
  return triggerRequest.get<DefaultSuccess<any[]>>(`/api/company/company-configs?appId=com.maxhub.meetingclient&moduleKey=${moduleKey}`);
};

/**
 * 预约会议（普通会议）-V2
 */
export const orderMeetingV2 = (meetingV2: OrderMeetingV2) => {
  return request.post<DefaultSuccess<IMeetingDetail>>('/api/user/v2/meetings/actions/schedule', meetingV2);
};

/**
 * 预约周期会议-V2
 */
export const orderRepeatMeetingV2 = (meetingV2: OrderMeetingV2) => {
  return request.post<DefaultSuccess<IMeetingDetail>>('/api/user/v2/repeat-meetings/actions/schedule', meetingV2);
};

/**
 * 修改会议（普通会议）-V2
 */
export const updateMeetingV2 = (meetingNo: string, meetingV2: OrderMeetingV2) => {
  return request.put<DefaultSuccess<IMeetingDetail>>('/api/user/v2/meetings/actions/update', {
    meetingNo,
    ...meetingV2
  });
};

/**
 * 更新单场会议（周期会议）-V2
 */
export const updateSingleRepeatMeetingV2 = (meetingNo: string, meetingV2: OrderMeetingV2) => {
  return request.put<DefaultSuccess<IMeetingDetail>>('/api/user/v2/repeat-meetings/actions/update-single', {
    meetingNo,
    ...meetingV2
  });
};

/**
 * 更新全部会议（周期会议）-V2
 */
export const updateAllRepeatMeetingV2 = (repeatMeetingNo: string, meetingV2: OrderMeetingV2) => {
  return request.put<DefaultSuccess<IMeetingDetail>>('/api/user/v2/repeat-meetings/actions/update-repeat', {
    repeatMeetingNo,
    ...meetingV2
  });
};

/**
 * 查询会议
 * @param meetingNo 会议id
 */
export const searchMeetingV2 = (meetingNo: string) => {
  return request.get<DefaultSuccess<Meeting>>(`/api/user/v2/meetings/${meetingNo}`);
};

/**
 * 获取企业预约配置
 */
export const getOrderSettings = () => {
  return request.get<DefaultSuccess<{ template: string }>>('/api/user/company/configurations/v2/for-schedule');
};

/**
 * 获取用户关系
 */
export const getUserRelation = (personStaffId: string) => {
  return request.get<DefaultSuccess<IRelations>>(`/api/personal_contacts/${personStaffId}/action/relations`);
};

/**
 * 获取会议配置（过渡方案）
 */
export const getMeetingConfig = () => {
  return bffRequest.get<DefaultSuccess<Pick<OrderSettings, 'linkList'>>>('/bff/api/meeting-config');
};

// 添加文件到会议文件夹
export const associationFolder = (meetingNo: string, meetingFile: IMeetingFile) => {
  return request.post<DefaultSuccess>(`/api/user/meetings/${meetingNo}/files/folder`, [meetingFile]);
};

/**
 * 获取最近会议人列表
 */
export const getRecentlyList = (spaceTime = 90, themeLike = '') => {
  const nowSpaceTime = spaceTime || 90;
  const today = new Date();
  // 获取最近一个月的会议信息
  const sevenDaysAgo = new Date(today.getTime() - nowSpaceTime * 24 * 60 * 60 * 1000).getTime();
  return request.get<DefaultSuccess>(`/api/user/meeting-records/recent/list?recentBeginTime=${sevenDaysAgo}&themeLike=${themeLike}`);
};
// 获取人员日程列表
export const getMemberSchedules = (params: any) => {
  return request.post<DefaultSuccess<IMemberScheduleDetail[]>>('/api/user/meeting-records/schedule/meeting-participant/list', params);
};

/** 获取用户会议预约配置 */
export const getUserMeetingOrderConfig = () => {
  return request.get<DefaultSuccess<any>>(`/api/user/company/configurations/for-schedule`);
};
